/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { gnoment, gnomentToCalendarDate, calendarDateToGnoment } from 'shared-ui/gnoment';
import get from 'lodash/get';
import last from 'lodash/last';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';

import {
    SecurityHelper,
    ScrollToHelper,
    Spinner,
    Select,
    Stack,
    Inline,
    Box,
    AvailabilityTypes,
    DatePicker,
    localToday,
} from 'sarsaparilla';
import { withCompatRouter, COMPAT_ROUTER_PROP_TYPES } from 'sarsaparilla/legacy';

import { withRecaptchaFallback } from 'shared-ui/recaptcha';
import { authModalActions } from 'shared-ui/auth/redux';
import AvailabilityMonths from '../../shared-components/detailed-availability/AvailabilityMonthsNew';
import DetailedAvailabilityPageLayout, {
    LayoutControlContext,
} from '../../shared-components/detailed-availability/DetailedAvailabilityPageLayout';
import DetailedAvailabilityPageSide from '../../shared-components/detailed-availability/DetailedAvailabilityPageSide';
import DetailedAvailabilityPageMain from '../../shared-components/detailed-availability/DetailedAvailabilityPageMain';
import DetailedAvailabilityFilter from '../../shared-components/detailed-availability/DetailedAvailabilityFilter';
import DetailedAvailabilityFilterItem from '../../shared-components/detailed-availability/DetailedAvailabilityFilterItem';
import DetailedAvailabilityGridAndActions from '../../shared-components/detailed-availability/DetailedAvailabilityGridAndActions';
import DetailedAvailabilityInformationRequired from '../../shared-components/detailed-availability/DetailedAvailabilityInformationRequired';
import DetailedAvailabilityNavigator from '../../shared-components/detailed-availability/DetailedAvailabilityNavigator';
import { shouldUseSmallerGrid } from '../../shared-components/utils/layoutUtils';
import { isDaySameAsTodayOnUTC } from '../../shared-components/utils/availability_utils';
import {
    loadPermitMapping,
    permitContentSelector,
} from '../../shared-components/slices/permitContent';

import DivisionDetailsModal from '../../shared-components/division-details/DivisionDetailsModal';
import { getGateA } from '../../shared-components/utils/gateA';
import RecaptchaChallengeModal from '../components/RecaptchaChallengeModal';

import * as pActions from '../actions/permit';
import * as aActions from '../actions/availability';
import * as recaptchaConstants from '../constants/recaptcha';
import {
    RULE_NOT_SPECIFIC_TO_DIVISION,
    readRuleValue,
    readRule,
    isEntryDateOnly,
    getCurrentTime,
    issuanceStatuses,
    addFilterValues,
    convertDateToNearestISOString,
    readRuleAsMoment,
    readRuleAsBoolean,
    shouldIncrementCheckoutDate,
    getMaxMemberQuota,
    hasRule,
    getParameterByName,
    errorsToStringList,
} from '../utils/utilsNew';
import * as types from '../constants';
import * as SHAPES from '../props/permit';
import {
    getFilterErrors,
    seedQuotaFilters,
    seedExistingQuotaFilters,
    readQuotaFilters,
    isDayBlocked,
    isDayManagedByLottery,
    hasAccessToPermit,
    shouldIgnoreQuotaAfterFirstDay,
} from '../utils/availability-utils-new';
import AvailabilityGrid from '../components/detailed-availability-page-new/AvailabilityGrid';
import OlympicDistrictPicker from '../components/detailed-availability-page-new/OlympicDistrictPicker';
import BookNow from '../components/detailed-availability-page-new/BookNow';
import AvailabilityFilters from '../components/detailed-availability-page-new/AvailabilityFilters';
import TripItinerary from '../components/detailed-availability-page-new/TripItinerary';
import UnauthorizedErrorModal from '../components/detailed-availability-page-new/UnauthorizedErrorModal';
import OrganizedGroupQuestion from '../components/detailed-availability-page-new/OrganizedGroupQuestion';

const DAYS_TO_SKIP = 5;
const PERMITS_WITH_AREA_REQUIREMENT = [
    types.OLYMPIC_ID,
    types.CASCADES_ADVANCED_ID,
    types.CASCADES_DAILY_ID,
];
const PERMITS_WITH_DISTRICT_PICKER = [
    types.OLYMPIC_ID,
    types.CASCADES_ADVANCED_ID,
    types.CASCADES_DAILY_ID,
];
const PERMITS_WITH_CHILD_DISTANCES = [
    types.OLYMPIC_ID,
    types.CASCADES_ADVANCED_ID,
    types.CASCADES_DAILY_ID,
];

export class DetailedAvailabilityPage extends React.PureComponent {
    static propTypes = {
        router: COMPAT_ROUTER_PROP_TYPES,
        availabilities: SHAPES.AVAILABILITIES,
        account: PropTypes.shape({}),
        issuance: PropTypes.shape({
            division_id: PropTypes.string,
            id: PropTypes.string,
            start_date: PropTypes.string,
            end_date: PropTypes.string,
            permit_type: PropTypes.string,
            itinerary: PropTypes.arrayOf(
                PropTypes.shape({
                    start_date: PropTypes.string,
                    end_date: PropTypes.string,
                })
            ),
            status: PropTypes.number,
            equipments: PropTypes.array,
            permit_id: PropTypes.string,
        }),
        error: PropTypes.shape({}),
        issuanceError: PropTypes.shape({}),
        fetchIssuanceError: PropTypes.shape({}),
        updateIssuanceError: PropTypes.shape({}),
        updateIssuanceResult: PropTypes.string,
        permit: SHAPES.PERMIT,
        divisionStayLimitRule: PropTypes.shape({
            value: PropTypes.number,
        }),
        aggSeasonRules: PropTypes.array,
        dispatch: PropTypes.func,
        permitMapping: PropTypes.shape({}),
        recaptcha: PropTypes.object.isRequired,
    };

    static defaultProps = {
        permit: {},
    };

    constructor(props) {
        super(props);

        this.shouldShowGrid = false;

        this.state = {
            hasSeededQuotaValues: false,
            contactFacility: '',
            dataSource: [],
            didFetchAvailabilities: false,
            didGenerateDataSource: false,
            detailedAvailabilityEndDate: this.getStartDate().add(10, 'days'),
            detailedAvailabilityStartDate: this.getStartDate(),
            isGroup: false,
            displayWarning: false,
            divisionsForCurrentSection: [],
            existingItinerary: [],
            existingReservationId:
                this.props.router.searchParams.get('reservationId') || '',
            fetchedAvailability: false,
            initialQuotaFilterValues: {},
            isLoading: false,
            isProcessing: false,
            jumpDateFocused: false,
            loginModalShown: false,
            monthsFetched: [],
            equipments: [],
            selectedSection: this.props.router.searchParams.get('section'),
            selectedDistricts: [],
            selectedItinerary: [],
            selectedQuotaToDisplay: {},
            quotaFilterValues: {},
            showCommercialAuthModal: false,
            sort: {
                column: 'view_order',
                isSorted: true,
                isAsc: true,
            },
            startNextDay: false,
            selectedDivisionId: '',
            isDivisionDetailsModalOpen: false,
            isChallengeModalOpen: false,
        };

        this.updateQuotaFilterValue = this.updateQuotaFilterValue.bind(this);
        this.updateQuotaFilterType = this.updateQuotaFilterType.bind(this);
        this.handleClearSelection = this.handleClearSelection.bind(this);
        this.handleNextAvailableClick = this.handleNextAvailableClick.bind(this);
        this.handleStartDateChange = this.handleStartDateChange.bind(this);
        this.handleJumpDateFocusChange = this.handleJumpDateFocusChange.bind(this);
        this.handleRegistration = this.handleRegistration.bind(this);
        this.handleUpdateSort = this.handleUpdateSort.bind(this);
        this.handleSectionSelection = this.handleSectionSelection.bind(this);
        this.handleQuestionResponse = this.handleQuestionResponse.bind(this);
        this.handleSelection = this.handleSelection.bind(this);

        this.errorsRef = React.createRef();
    }

    componentDidMount() {
        const sectionParam = this.props.router.searchParams.get('section');
        if (
            window.sessionStorage.getItem('permitId') !==
            this.props.router.params.permitId
        ) {
            // reset sessionstorage because you have switched permits
            this.clearSessionStorageForAvailabilityGrid();
        } else {
            const equipments =
                JSON.parse(window.sessionStorage.getItem('equipments')) || [];
            const selectedSection =
                JSON.parse(window.sessionStorage.getItem('selectedSection')) ||
                sectionParam;
            const selectedDistricts =
                JSON.parse(window.sessionStorage.getItem('selectedDistricts')) || [];
            const selectedItinerary =
                JSON.parse(window.sessionStorage.getItem('selectedItinerary')) || [];
            const selectedQuotaToDisplay =
                JSON.parse(window.sessionStorage.getItem('selectedQuotaToDisplay')) || {};
            const quotaFilterValues =
                JSON.parse(window.sessionStorage.getItem('quotaFilterValues')) || {};

            this.setState({
                equipments,
                selectedSection,
                selectedDistricts,
                selectedItinerary,
                selectedQuotaToDisplay,
                quotaFilterValues,
            });
        }

        window.sessionStorage.setItem('permitId', this.props.router.params.permitId);
        // If the user set a permit type on the main page, put it in storage
        if (
            sectionParam &&
            !JSON.parse(window.sessionStorage.getItem('selectedSection'))
        ) {
            window.sessionStorage.setItem(
                'selectedSection',
                JSON.stringify(sectionParam)
            );
        }

        if (
            !hasAccessToPermit(
                SecurityHelper.getAccount(),
                this.props.router.params.permitId
            )
        ) {
            this.blockAccessToCommercial();
            return;
        }

        this.props.dispatch(loadPermitMapping());

        // Get the stay limit for the permit
        this.props.dispatch(
            pActions.fetchStayLimitRule({
                permit_id: this.props.router.params.permitId,
                division_id: undefined,
                start_date: this.state.detailedAvailabilityStartDate,
            })
        );

        // clear availabilities in case availability data from another permit carries over
        this.props.dispatch(aActions.clearAvailabilitiesMap());

        // once we fetch the permit data, we restore settings saved in session storage
        if (this.props.router.params.permitId) {
            const apiCalls = [
                this.props.dispatch(
                    pActions.fetchPermit(this.props.router.params.permitId)
                ),
                this.props.dispatch(
                    pActions.fetchAggregateSeason(this.props.router.params.permitId)
                ),
            ];

            Promise.all(apiCalls).then(() => {
                this.parsePermit();

                const sessionStorageStartDate = JSON.parse(
                    window.sessionStorage.getItem('detailedAvailabilityStartDate')
                );
                const date = this.props.router.searchParams.get('date');

                // Restore date from session storage ...
                if (sessionStorageStartDate) {
                    this.handleStartDateChange(sessionStorageStartDate);
                }
                // ... or check params for a date
                else if (date) {
                    this.handleStartDateChange(date);
                }
            });
        }
    }

    componentDidUpdate(prevProps, prevState) {
        if (!prevProps.account && this.props.account) {
            if (!hasAccessToPermit(SecurityHelper.getAccount(), this.props.permit.id)) {
                this.blockAccessToCommercial();
                return;
            }

            if (
                SecurityHelper.isCommercialAccount() &&
                prevState.selectedItinerary.length !== this.state.selectedItinerary.length
            ) {
                this.props.dispatch(aActions.clearAvailabilitiesMap());
                this.setState({ selectedItinerary: [] });
            }

            if (this.state.fetchedAvailability) {
                const issuanceId = this.state.existingReservationId.split(
                    types.RES_ID_DELIMITER
                )[0];
                this.fetchAvailability({
                    props: this.props,
                    permitId: this.props.router.params.permitId,
                    startDate: this.getStartDate(this.props),
                    commercialAccountFlag: SecurityHelper.isCommercialAccount(),
                    addQuotaId: issuanceId,
                });
            }

            if (!SecurityHelper.isCommercialAccount() && this.state.loginModalShown) {
                this.navigateToRegistration();
            }
        }

        // If the itinerary has been cleared, make sure the local storage version is also cleared
        if (
            this.state.selectedItinerary.length === 0 &&
            window.sessionStorage.getItem('selectedItinerary')
        ) {
            window.sessionStorage.removeItem('selectedItinerary');
        }

        // If availabilities have changed (and there's availabilities to use)
        if (
            this.props.availabilities !== prevProps.availabilities &&
            Object.keys(this.props.availabilities).length
        ) {
            let filterValues = {};

            if (this.state.existingReservationId) {
                filterValues = seedExistingQuotaFilters(
                    this.props.availabilities,
                    get(this.props, 'permit'),
                    this.state.selectedQuotaToDisplay,
                    this.state.quotaFilterValues,
                    get(this.props, 'issuance'),
                    this.state.guidesCountTowardsGroupSize
                );
            } else {
                filterValues = seedQuotaFilters(
                    this.props.availabilities,
                    get(this.props, 'permit'),
                    this.state.selectedQuotaToDisplay,
                    this.state.quotaFilterValues
                );
            }

            this.setState(
                {
                    selectedQuotaToDisplay: filterValues.mainFilterType,
                    quotaFilterValues: filterValues.filterValues,
                    initialQuotaFilterValues: filterValues.filterValues,
                    hasSeededQuotaValues: true,
                },
                () => {
                    this.generateDataSource();
                }
            );
        }

        // If there's an error
        if (this.props.error && this.props.error !== prevProps.error) {
            const errorDiv = document.querySelector('.permit-notifications-errs');
            const scrollingDiv = document.querySelector(
                '.per-detailed-availability-content'
            );

            scrollingDiv.scrollTop = errorDiv.offsetTop;
            ScrollToHelper.scrollTo(0, 0);
        }

        // If the start date as changed, fetch new availabilities
        if (
            !prevState.detailedAvailabilityStartDate.isSame(
                this.state.detailedAvailabilityStartDate
            )
        ) {
            const thisMonth = this.state.detailedAvailabilityStartDate
                .clone()
                .utc()
                .startOf('month');
            const nextMonth = thisMonth.clone().utc().add(1, 'month').startOf('month');

            const thisMonthUnfetched = !this.state.monthsFetched.includes(
                thisMonth.format('YYYY-MM')
            );
            const nextMonthUnfetched = !this.state.monthsFetched.includes(
                nextMonth.format('YYYY-MM')
            );

            const goingForwards = this.isMonthBoundary() === 1;
            const goingBackwards = this.isMonthBoundary() === -1;

            const largeSpan =
                this.state.detailedAvailabilityStartDate.diff(
                    prevState.detailedAvailabilityStartDate.format(),
                    'days'
                ) > DAYS_TO_SKIP;
            const missingMonth =
                thisMonthUnfetched &&
                this.state.detailedAvailabilityStartDate.format('YYYY-MM') ===
                    thisMonth.format('YYYY-MM');

            const fetchForward = goingForwards && nextMonthUnfetched && !largeSpan;
            const fetchBackward =
                (goingBackwards && thisMonthUnfetched) || largeSpan || missingMonth;

            if (fetchForward || fetchBackward) {
                this.fetchAvailability({
                    props: this.props,
                    permitId: this.props.router.params.permitId,
                    startDate: fetchForward ? nextMonth : thisMonth,
                    commercialAccountFlag: SecurityHelper.isCommercialAccount(),
                    addQuotaId: this.state.existingReservationId.split(
                        types.RES_ID_DELIMITER
                    )[0],
                    fetchAdjacent: true,
                });
            }
        }

        // For Olympic/Ruby: When itinerary changes, regenerate data in to only show reachable districts
        if (!isEqual(this.state.selectedItinerary, prevState.selectedItinerary)) {
            this.generateDataSource();
        }
    }

    componentWillUnmount() {
        this.props.dispatch(pActions.clearError());
        this.props.dispatch(pActions.clearFetchIssuanceError());
        this.props.dispatch(pActions.clearUpdateIssuanceError());
    }

    clearSessionStorageForAvailabilityGrid() {
        window.sessionStorage.removeItem('permitId');
        window.sessionStorage.removeItem('selectedItinerary');
        window.sessionStorage.removeItem('selectedQuotaToDisplay');
        window.sessionStorage.removeItem('quotaFilterValues');
        window.sessionStorage.removeItem('selectedSection');
        window.sessionStorage.removeItem('selectedDistricts');
        window.sessionStorage.removeItem('equipments');
        window.sessionStorage.removeItem('detailedAvailabilityStartDate');
    }

    isMonthBoundary = () => {
        const dateMapDates = this.getDateMapDates();

        if (dateMapDates.length) {
            if (
                this.state.detailedAvailabilityEndDate.isAfter(
                    gnoment(last(dateMapDates))
                        .endOf('month')
                        .subtract(DAYS_TO_SKIP, 'day')
                )
            ) {
                return 1;
            }
            if (
                this.state.detailedAvailabilityStartDate
                    .clone()
                    .add(DAYS_TO_SKIP, 'day')
                    .isBefore(dateMapDates[0])
            ) {
                return -1;
            }
            return 0;
        }

        const startDayString = this.state.detailedAvailabilityStartDate.format('D');

        if (parseInt(startDayString, 10) < 15) {
            return -1;
        }
        return 1;
    };

    focusErrorNotification = () => {
        if (this.errorsRef.current) this.errorsRef.current.focus();
    };

    getDateMapDates = () =>
        Object.keys(get(this.state, ['dataSource', 0, 'dateMap'], {}));

    getStartDate = (props = this.props) => {
        let source;
        const dateParam = props.router.searchParams.get('date');
        if (dateParam) {
            source = gnoment(dateParam);
        } else {
            source = getCurrentTime(props);
        }
        return source.utc(true).clone().startOf('day');
    };

    getSectionOptions() {
        const permitSections = get(this.props, 'permit.permit_sections', []);
        const options = permitSections.map((section) => ({
            value: section.name,
            label: section.name,
        }));

        return options;
    }

    getOverlay() {
        if (
            get(this.props, 'permit.components.permit_section_filter') &&
            this.state.selectedSection === ''
        ) {
            return 'Please select Permit type';
        }
        return getFilterErrors(this.state.quotaFilterValues)[0];
    }

    setIsProcessing = () => {
        if (!this.state.isProcessing) {
            this.setState({
                isProcessing: true,
            });
        }
    };

    blockAccessToCommercial() {
        this.setState({ showCommercialAuthModal: true });
    }

    parsePermit = () => {
        if (get(this.props, 'permit.components.reservation_type_question')) {
            this.setState({ isGroup: true, contactFacility: '' });
        }

        this.updateRules();

        // read recarea
        let recId = 0;
        if (get(this.props, 'permit.recareas[0]')) {
            recId = this.props.permit.recareas[0];
        }
        this.props.dispatch(pActions.fetchRecarea(recId));
        // set equipments from permit
        this.setState({
            equipments: this.props.permit.equipments,
        });
        // fetch issuance if we're in a modify quota state
        if (this.state.existingReservationId) {
            this.fetchExistingRes();
        } else {
            // read availabilities for all divisions at once
            this.fetchAvailability({
                props: this.props,
                permitId: this.props.router.params.permitId,
                startDate: this.getStartDate(),
                commercialAccountFlag: SecurityHelper.isCommercialAccount(),
                addQuotaId: '',
                fetchAdjacent: true,
            });
        }
    };

    fetchAdjacentMonth = (date) => {
        // If we're at a month boundary, fetch the next one.

        if ([1, -1].includes(this.isMonthBoundary())) {
            const startDate = date.add(this.isMonthBoundary(), 'month').startOf('month');
            this.fetchAvailability({
                props: this.props,
                permitId: this.props.router.params.permitId,
                startDate,
                commercialAccountFlag: SecurityHelper.isCommercialAccount(),
                addQuotaId: this.state.existingReservationId.split(
                    types.RES_ID_DELIMITER
                )[0],
            });
        }
    };

    updateRules = () => {
        const { permit } = this.props;
        const rules = get(permit, 'rules', []);

        const guidesCountTowardsGroupSize = hasRule(
            types.GUIDES_COUNT_TOWARDS_GROUP_SIZE,
            rules
        );
        const disableGroupList = hasRule('DisableGroupList', rules);
        const ruleLotteryRegistrationOnly = hasRule(
            types.LOTTERY_REGISTRATION_ONLY,
            rules
        );
        const lotteryOnlyError = ruleLotteryRegistrationOnly
            ? 'This permit can only be purchased through the lottery.'
            : '';

        const itineraryStayLimitPerDivision =
            readRuleValue(types.ITINERARY_STAY_LIMIT_PER_DIVISION, rules) /
                types.DAYS_TO_SECS || undefined;
        const stayLimit = Number(
            readRuleValue(
                types.STAY_LIMIT_RULE_NAME,
                this.props.permit.rules,
                RULE_NOT_SPECIFIC_TO_DIVISION
            ) / types.DAYS_TO_SECS
        );

        // maybe put this in a ruleMap?
        this.setState({
            guidesCountTowardsGroupSize,
            disableGroupList,
            lotteryOnlyError,
            itineraryStayLimitPerDivision,
            stayLimit,
        });
    };

    fetchExistingRes = () => {
        // load existing issuance
        const issuanceId = this.state.existingReservationId.split(
            types.RES_ID_DELIMITER
        )[0];
        const permitId = this.state.existingReservationId.split(
            types.RES_ID_DELIMITER
        )[1];
        const requiresDistrictPicker = PERMITS_WITH_DISTRICT_PICKER.includes(permitId);
        this.props
            .dispatch(pActions.fetchUnpaidIssuance(issuanceId, permitId))
            .then(() => {
                if (!this.props.fetchIssuanceError) {
                    // read availabilities for all divisions at once
                    this.fetchAvailability({
                        props: this.props,
                        permitId: this.props.router.params.permitId,
                        startDate: this.getStartDate(),
                        commercialAccountFlag: SecurityHelper.isCommercialAccount(),
                        addQuotaId: issuanceId,
                        fetchAdjacent: true,
                    });

                    // Populate the permit type dropdown
                    const permitSections = get(this.props.permit, 'permit_sections');
                    const firstItineraryItem = this.props.issuance.itinerary[0];

                    if (permitSections && permitSections.length && firstItineraryItem) {
                        let selectedSection = '';

                        for (let i = 0; i < permitSections.length; i += 1) {
                            const divisionIds = permitSections[i].division_ids;
                            if (divisionIds.includes(firstItineraryItem.division_id)) {
                                selectedSection = permitSections[i].name;
                                break;
                            }
                        }

                        this.setState({
                            selectedSection,
                        });
                    }

                    // Populate the Olympic District picker
                    if (requiresDistrictPicker && firstItineraryItem) {
                        const firstDivision =
                            this.props.permit.divisions[firstItineraryItem.division_id];

                        this.setState({
                            selectedDistricts: [firstDivision.district],
                        });
                    }

                    const itinerary = [];
                    const entryDateOnly = isEntryDateOnly(
                        this.props.permit.rules,
                        Object.keys(this.props.permit.divisions)
                    );

                    const permit = this.props.permit;
                    if (!entryDateOnly) {
                        // multi-day itinerary
                        this.props.issuance.itinerary.forEach((item) => {
                            itinerary.push({
                                startDate: gnoment(item.start_date)
                                    .utc()
                                    .format(types.AVAILABILITY_KEY_MAP_FORMAT),
                                endDate: gnoment(item.end_date)
                                    .utc()
                                    .format(types.AVAILABILITY_KEY_MAP_FORMAT),
                                division: this.props.permit.divisions[item.division_id],
                                permit,
                            });
                        });
                    } else {
                        const division =
                            this.props.permit.divisions[this.props.issuance.division_id];
                        const item = {
                            startDate: gnoment(this.props.issuance.start_date)
                                .utc()
                                .format(types.AVAILABILITY_KEY_MAP_FORMAT),
                            endDate: gnoment(this.props.issuance.start_date)
                                .utc()
                                .format(types.AVAILABILITY_KEY_MAP_FORMAT),
                            division,
                            permit,
                        };
                        itinerary.push(item);
                    }
                    this.handleSelection(itinerary, false);

                    const clearDates =
                        'Reservations must be cancelled to change the start date.';
                    let modifyWarning = `Start date of reservation cannot be modified. ${clearDates}`;
                    if (
                        this.props.issuance.status ===
                            issuanceStatuses.get(types.PERMIT_PRELIMINARY) ||
                        this.props.issuance.status ===
                            issuanceStatuses.get(types.PERMIT_IN_PROGRESS)
                    ) {
                        modifyWarning = clearDates;
                    }
                    this.setState(
                        {
                            equipments: this.props.issuance.equipments,
                            existingItinerary: itinerary,
                            displayWarning: true,
                            warning: modifyWarning,
                        },
                        () => {
                            // halve equipment quotas if we're Pere Marquette
                            if (
                                this.props.issuance.permit_id === types.PERE_MARQUETTE_ID
                            ) {
                                const { equipments } = this.updatePereMarquetteFilter(
                                    this.props.issuance,
                                    true
                                );
                                this.setState({ equipments });
                            }
                        }
                    );
                } else {
                    // issuance not found or cancelled/removed from cart - do default behavior
                    this.fetchAvailability({
                        props: this.props,
                        permitId: this.props.router.params.permitId,
                        startDate: this.getStartDate(),
                        commercialAccountFlag: SecurityHelper.isCommercialAccount(),
                        addQuotaId: '',
                        fetchAdjacent: true,
                    });
                }
            })
            .catch(() => {
                // avoid throwing error up
                // reservation failed to load, will be handled by reducer GET_ISSUANCE_ERROR
            });
    };

    fetchAvailability({
        permitId,
        startDate,
        commercialAccountFlag,
        addQuotaId,
        fetchAdjacent,
    }) {
        if (this.state.monthsFetched.includes(startDate.format('YYYY-MM'))) {
            return;
        }

        this.setState({
            fetchedAvailability: true,
            monthsFetched: [
                ...this.state.monthsFetched,
                startDate.clone().startOf('month').format('YYYY-MM'),
            ],
        });

        if (!startDate.isValid() || this.state.lotteryOnlyError) return;

        this.setState({
            isProcessing: true,
        });

        this.props
            .dispatch(
                aActions.fetchAvailabilityAllDivisions({
                    permitId,
                    startDate,
                    commercialAccountFlag,
                    addQuotaId,
                })
            )
            .then(() => {
                if (fetchAdjacent) {
                    this.fetchAdjacentMonth(startDate);
                }
            })
            .then(() => {
                this.setState({
                    isProcessing: false,
                    didFetchAvailabilities: true,
                });
            });
    }

    handleDisplayWarning = (flag, message) => {
        this.setState({ displayWarning: flag, warning: message });
    };

    handleSectionSelection(e) {
        const selectedSection = e.target.value;

        this.setState({ selectedSection }, () => {
            // store selection in local storage
            window.sessionStorage.setItem(
                'selectedSection',
                JSON.stringify(selectedSection)
            );

            // generate a new data source
            this.generateDataSource();

            // and clear out any itinerary
            this.setState({
                selectedItinerary: [],
            });
        });
    }

    getDivisionsForSection(dataSource) {
        const divisionsForCurrentSection = [];

        if (dataSource && dataSource.length) {
            dataSource.forEach((item) => {
                divisionsForCurrentSection.push(item.division ? item.division.id : '');
            });
        }

        return divisionsForCurrentSection;
    }

    resetSelection() {
        const { selectedItinerary } = this.state;
        if (selectedItinerary.length) {
            this.setState({
                selectedItinerary: [],
            });
        }
    }

    handleSelection(newItinerary, shouldUpdateDivisionStayLimit) {
        if (shouldUpdateDivisionStayLimit) {
            this.updateStayLimit(newItinerary);
        }

        // array of objects needs a deep clone to lost references
        const selectedItinerary = cloneDeep(newItinerary);
        this.setState({ selectedItinerary });

        try {
            window.sessionStorage.setItem(
                'selectedItinerary',
                JSON.stringify(selectedItinerary)
            );
        } catch (e) {
            // Avoid throwing an error if we run out of local storage.
            // Instead fail quietly and maybe nobody will even notice
        }
    }

    updateStayLimit(itinerary) {
        if (
            itinerary &&
            itinerary.length &&
            this.props.permit.components.reload_stay_limit
        ) {
            const curItinerary = itinerary[itinerary.length - 1];
            const issObj = {
                permit_id: curItinerary.permit.id,
                division_id: curItinerary.division.id,
                start_date: gnoment.utc(curItinerary.startDate),
            };
            this.props.dispatch(pActions.fetchStayLimitRule(issObj)).then(() => {
                const dataSource = this.state.dataSource.slice();
                for (let i = 0; i < dataSource.length; i++) {
                    if (dataSource[i].division.id === curItinerary.division.id) {
                        dataSource[i].division.stayLimit =
                            this.props.divisionStayLimitRule.value / 24 / 60 / 60;
                    }
                }
                this.setState({ dataSource });
            });
        }
    }

    handleClearSelection() {
        // clear selection, sort by view_order
        this.setState({
            sort: {
                column: 'view_order',
                isSorted: true,
                isAsc: true,
            },
            selectedItinerary: [],
        });

        window.sessionStorage.removeItem('selectedItinerary');
    }

    handleDistrictFilterClick = (district) => {
        const { selectedDistricts } = this.state;

        if (selectedDistricts.includes(district)) {
            return;
        }

        const newSelectedDistricts = [district];

        this.setState(
            {
                selectedDistricts: newSelectedDistricts,
                selectedItinerary: [],
            },
            () => {
                this.generateDataSource();
            }
        );

        window.sessionStorage.setItem(
            'selectedDistricts',
            JSON.stringify(newSelectedDistricts)
        );
    };

    handleNextAvailableClick() {
        // loop through dates from current startDate into the future.
        // check each division for quota availability vs. quotaFilters on that date.
        // short circuit on first true.
        const { availabilities } = this.props;
        const { quotaFilterValues } = this.state;
        const quotaTypeKeys = Object.keys(quotaFilterValues).filter(
            (key) => key !== 'guides'
        );
        const quotaTypeKeysLength = quotaTypeKeys.length;

        const dayIterator = this.state.detailedAvailabilityStartDate.clone();
        const today = gnoment().utc().startOf('day');
        const lastDay = readRuleAsMoment(
            'SeasonEnd',
            this.props.aggSeasonRules,
            this.props.permit.time_zone
        )
            .clone()
            .startOf('day')
            .utc();
        let foundDate;
        let dayString;

        const checkAllDivs = (divisionKey) => {
            const { quota_type_maps: quotaTypeMaps } = availabilities[divisionKey].result;
            let avail = 0;
            const guideFilterValue = quotaFilterValues.guides || 0;
            quotaTypeKeys
                .filter((key) => key !== 'guides')
                .forEach((quotaTypeKey) => {
                    if (
                        quotaFilterValues[quotaTypeKey] + guideFilterValue <=
                        get(
                            quotaTypeMaps,
                            `[${quotaTypeKey}][${dayString}].remaining`,
                            -1
                        )
                    ) {
                        avail += 1;
                    }
                });
            if (
                avail === quotaTypeKeysLength &&
                !isDayManagedByLottery(dayIterator, this.props)
            ) {
                foundDate = dayIterator;
            }
        };

        while (!foundDate && dayIterator.isBefore(lastDay)) {
            dayIterator.add(1, 'day');
            if (dayIterator.isBefore(today)) {
                continue;
            }
            dayString = dayIterator.format();
            Object.keys(availabilities).forEach(checkAllDivs);
        }

        if (foundDate) {
            this.handleStartDateChange(foundDate);
        } else {
            this.setState({
                displayWarning: true,
                warning: 'There are no further available dates.',
            });
        }
    }

    handleStartDateChange(date) {
        if (!gnoment(date).isValid()) return;

        const detailedAvailabilityStartDate = gnoment(date).utc(true).startOf('day');

        this.setState(
            {
                detailedAvailabilityStartDate,
                detailedAvailabilityEndDate: detailedAvailabilityStartDate
                    .clone()
                    .add(10, 'days'),
            },
            () => {
                this.generateDataSource();
                this.updateStayLimit(this.state.selectedItinerary);
                window.sessionStorage.setItem(
                    'detailedAvailabilityStartDate',
                    JSON.stringify(
                        this.state.detailedAvailabilityStartDate.format(
                            types.YEAR_MONTH_DAY_DASH_FORMAT
                        )
                    )
                );
            }
        );
    }

    handleJumpDateFocusChange({ focused }) {
        this.setState({ jumpDateFocused: focused });
    }

    handleRegistration() {
        const loggedIn = SecurityHelper.isLoggedIn();
        if (!loggedIn) {
            this.setState({ loginModalShown: true });
            this.props.dispatch(authModalActions.openLoginModal());
            return;
        }

        if (!hasAccessToPermit(SecurityHelper.getAccount(), this.props.permit.id)) {
            this.blockAccessToCommercial();
            return;
        }
        this.navigateToRegistration();
    }

    handleUpdateSort(sort) {
        this.setState({
            sort,
        });
    }

    handleQuestionResponse(event) {
        const contactFacility = event.target.value;
        switch (contactFacility) {
            case 'yes':
                this.setState({ isGroup: true, contactFacility });
                break;
            default:
                this.setState({ isGroup: false, contactFacility: 'no' });
                break;
        }
    }

    handleDivisionDetailsClick = (divisionId) => {
        this.setState({
            selectedDivisionId: divisionId,
            isDivisionDetailsModalOpen: true,
        });
    };

    closeDivisionDetailsModal = () =>
        this.setState({ isDivisionDetailsModalOpen: false });

    async navigateToRegistration() {
        const { selectedItinerary: itinerary } = this.state;
        if (itinerary.length <= 0) {
            return;
        }

        // Defer to the v2 challenge modal when a challenge is already required.
        // Must run before isLoading is set, otherwise the early return leaves the
        // flag stuck and the modal's Continue button spins forever.
        if (
            this.props.recaptcha.isChallengeRequired &&
            !this.state.isChallengeModalOpen
        ) {
            this.setState({ isChallengeModalOpen: true });
            return;
        }

        this.setState({ isLoading: true });
        const firstItineraryItem = itinerary[0];
        const lastItineraryItem = itinerary[itinerary.length - 1];
        let endDate = gnoment(lastItineraryItem.endDate);

        // We only care about the entry date in this case. The user will be able to select an end date on the
        // permit registration page. Unless date has been set already on the permit registration page,
        // set the end date to undefined
        if (isEntryDateOnly(this.props.permit.rules, [firstItineraryItem.division.id])) {
            const queryDate = getParameterByName();
            if (queryDate && this.state.existingItinerary.length) {
                endDate = gnoment(queryDate);
            } else {
                endDate = undefined;
            }
        }

        let item = {
            permit_id: firstItineraryItem.permit.id,
            division_id: firstItineraryItem.division.id,
            user_id: SecurityHelper.getAccount().account_id,
            start_date: gnoment(firstItineraryItem.startDate),
            end_date: endDate,
            status: issuanceStatuses.get(types.PERMIT_PRELIMINARY),
            permit_type: this.state.selectedSection,
            animals: [],
            pets: [],
        };

        item = Object.assign(item, addFilterValues(this.state.quotaFilterValues));

        // double equipment quotas if we're Pere Marquette
        if (item.permit_id === types.PERE_MARQUETTE_ID) {
            item = this.updatePereMarquetteFilter(item);
        }

        const itineraryEntries = itinerary.map((entry) => {
            return {
                start_date: convertDateToNearestISOString(gnoment(entry.startDate)),
                end_date: convertDateToNearestISOString(gnoment(entry.endDate)),
                division_id: entry.division.id,
            };
        });
        item.itinerary = itineraryEntries;

        const recaptcha = await getGateA(
            this.props.recaptcha,
            recaptchaConstants.CH_AVAILABILITY_BOOKING
        );

        this.dispatchIssuance(item, recaptcha);

        // Clear all session storage items
        this.clearSessionStorageForAvailabilityGrid();
    }

    getUpdatedExistingIssuanceGroupMembers = (issuance) => {
        let groupInfo = [];
        if (issuance.group_info) {
            groupInfo = issuance.group_info.map((member) => {
                const newMember = member;

                newMember.start_date = issuance.start_date;
                newMember.end_date = issuance.end_date;

                return newMember;
            });
            const minMemberQuota =
                parseInt(
                    readRuleValue(
                        'MinGroupSize',
                        this.props.permit.rules,
                        issuance.division_id
                    ),
                    10
                ) || 0;
            const maxMemberQuota = getMaxMemberQuota({
                permit: this.props.permit,
                issuance,
            });

            if (groupInfo.length > maxMemberQuota) {
                groupInfo = groupInfo.slice(0, maxMemberQuota);
            } else if (groupInfo.length < minMemberQuota) {
                const differenceInGroupSize = minMemberQuota - groupInfo.length;
                for (let i = 0; i < differenceInGroupSize; i++) {
                    groupInfo.push({ start_date: issuance.start_date });
                }
            }
        }

        return groupInfo;
    };

    dispatchIssuance(item, recaptcha) {
        if (
            this.state.existingReservationId &&
            this.props.issuance &&
            !this.props.fetchIssuanceError
        ) {
            //  use existing issuance and just update field values here
            let issuance = cloneDeep(this.props.issuance);

            issuance.itinerary = item.itinerary;
            issuance.start_date = item.start_date;
            issuance.division_id = item.division_id;
            issuance.end_date = item.end_date;
            issuance.is_modify_quota = true; // used to bypass backend multiple permit per day rule restriction
            issuance.group_info = this.getUpdatedExistingIssuanceGroupMembers(issuance);
            issuance.group_size = issuance.group_info.length;
            issuance.permit_type = item.permit_type;

            issuance = Object.assign(
                issuance,
                addFilterValues(
                    this.state.quotaFilterValues,
                    issuance.group_info,
                    issuance.guides
                )
            );
            // double equipment quotas if we're Pere Marquette
            if (item.permit_id === types.PERE_MARQUETTE_ID) {
                issuance = this.updatePereMarquetteFilter(issuance);
            }

            this.props
                .dispatch(pActions.updateIssuance(issuance))
                .then(() => {
                    this.props.dispatch(
                        pActions.saveFilters(this.state.quotaFilterValues)
                    );
                })
                .then(() => {
                    if (!this.props.updateIssuanceError) {
                        const reservationId = this.state.existingReservationId;
                        let redirectURL = `/permits/${this.props.permit.id}/registration/${reservationId}`;
                        if (
                            this.props.updateIssuanceResult ===
                            types.ISSUANCE_UPDATE_SUCCESSFUL
                        ) {
                            redirectURL = `/account/orders/${this.props.issuance.order_id}/reservations/${reservationId}?updateSuccessful=true`;
                            window.location.assign(redirectURL);
                        } else {
                            this.props.router.navigate(redirectURL);
                        }
                    } else {
                        this.setState({ isLoading: false });
                    }
                })
                .catch(() => {
                    this.focusErrorNotification();
                });
        } else {
            const issuance = { ...item };
            // set first group member name as the one belonging to the account
            if (!SecurityHelper.isCommercialAccount() && get(item.group_info, 'length')) {
                const { first_name, last_name } = SecurityHelper.getAccount();
                const firstGroupMember = {
                    ...issuance.group_info[0],
                    first_name,
                    last_name,
                };
                issuance.group_info[0] = firstGroupMember;
            }

            this.props
                .dispatch(
                    pActions.processBooking({
                        doRedirect: false,
                        item: issuance,
                        recaptcha,
                    })
                )
                .then(() => {
                    this.props.dispatch(
                        pActions.saveFilters(this.state.quotaFilterValues)
                    );
                })
                .then(() => {
                    if (!this.props.issuanceError) {
                        this.props.recaptcha.resetChallenge();
                        this.setState({ isChallengeModalOpen: false });
                        const reservationId = `${this.props.issuance.id}${types.RES_ID_DELIMITER}${this.props.permit.id}`;
                        const redirectURL = `/permits/${this.props.permit.id}/registration/${reservationId}`;
                        this.props.router.navigate(redirectURL);
                    }
                })
                .catch((err) => {
                    const { shouldRetry } =
                        this.props.recaptcha.handleRecaptchaError(err);
                    if (shouldRetry) {
                        this.setState({ isChallengeModalOpen: true, isLoading: false });
                        return;
                    }
                    this.setState({ isChallengeModalOpen: false, isLoading: false });
                    this.focusErrorNotification();
                });
        }
    }

    generateDataSource = () => {
        // Don't generate data if we haven't fetched data or if the user hasn't made selections yet
        if (!this.shouldShowGrid || !this.state.didFetchAvailabilities) {
            return;
        }

        const {
            quotaFilterValues,
            selectedQuotaToDisplay,
            detailedAvailabilityStartDate: startDate,
            detailedAvailabilityEndDate: endDate,
        } = this.state;

        const { permit, aggSeasonRules, availabilities } = this.props;

        let result = [];
        if (!permit || !permit.divisions || !availabilities) {
            const divisionsForCurrentSection = this.getDivisionsForSection(result);

            this.setState({
                dataSource: result,
                divisionsForCurrentSection,
            });

            return;
        }

        const { selectedDistricts, selectedItinerary } = this.state;
        const startDateString = startDate.format();
        const endDateString = endDate.format();
        const requiresDistrictPicker = PERMITS_WITH_DISTRICT_PICKER.includes(
            this.props.router.params.permitId
        );
        const requiresChildDistances = PERMITS_WITH_CHILD_DISTANCES.includes(
            this.props.router.params.permitId
        );

        // for every division
        result = Object.keys(permit.divisions).filter((divisionId) => {
            return (
                availabilities[divisionId] &&
                availabilities[divisionId].result &&
                permit.divisions[divisionId].is_active &&
                !permit.divisions[divisionId].is_hidden
            );
        });

        // Filtering for permit types picker (Fire Island, Olympic)
        if (get(this.props, 'permit.components.permit_section_filter')) {
            result = result.filter((divisionId) => {
                if (!get(this.props, 'permit.components.permit_section_filter')) {
                    return true;
                }
                const { selectedSection } = this.state;
                if (!selectedSection) {
                    // hasn't been selected yet ('' is false), display NONE.
                    return false;
                }
                const { permit_sections: permitSections } = this.props.permit;
                const currentDivisions = permitSections.find(
                    (type) => type.name === selectedSection
                ).division_ids;
                return currentDivisions.findIndex((div) => div === divisionId) !== -1;
            });
        }

        // Filtering for Olympic district picker (only for starting location)
        if (requiresDistrictPicker && selectedItinerary.length === 0) {
            result = result.filter((divisionId) => {
                if (!selectedDistricts.length) {
                    // no district is selected, so display NONE.
                    return false;
                }
                return selectedDistricts.includes(permit.divisions[divisionId].district);
            });
        }

        // Filtering for Olympic child_distance (only show reachable campsites)
        if (requiresChildDistances && selectedItinerary.length > 0) {
            result = result.filter((divisionId) => {
                // If the id is part of the itinerary, don't filter it out
                for (let i = 0; i < selectedItinerary.length; i += 1) {
                    if (selectedItinerary[i].division.id === divisionId) {
                        return true;
                    }
                }

                const lastItineraryItem = selectedItinerary[selectedItinerary.length - 1];

                if (
                    lastItineraryItem &&
                    get(lastItineraryItem, 'division.child_distances')
                ) {
                    const childIds = Object.keys(
                        lastItineraryItem.division.child_distances
                    );
                    return childIds.includes(divisionId);
                }

                return true;
            });
        }

        result = result.map((divisionId, index) => {
            // create a copy of that division
            const division = { ...permit.divisions[divisionId] };

            // assign a view_order if one is not yet set
            if (!division.view_order) {
                division.view_order = index + 1;
            }
            // assign a stay limit to that division
            let stayLimit =
                readRuleValue('StayLimit', permit.rules, divisionId) / 24 / 60 / 60; // seconds -> days

            if (shouldIgnoreQuotaAfterFirstDay(availabilities)) {
                stayLimit = Math.min(stayLimit, 1);
            }
            division.stayLimit = stayLimit;

            const maxGroupSize = readRuleValue('MaxGroupSize', permit.rules, divisionId);
            const minGroupSize = readRuleValue('MinGroupSize', permit.rules, divisionId);
            const resultingAvailableDays = { ...availabilities[divisionId].result.bools };
            const secretMap = { ...availabilities[divisionId].result.secret };
            const displayMap = {};
            const quotas = { ...availabilities[divisionId].result.quota_type_maps };
            // and assign to that division transformed availabilities
            Object.keys(availabilities[divisionId].result.quota_type_maps).forEach(
                (quotaType) => {
                    const filterValue = quotaFilterValues[quotaType];
                    const availabilitiesForQuotaType =
                        availabilities[divisionId].result.quota_type_maps[quotaType];

                    Object.keys(availabilitiesForQuotaType)
                        .sort()
                        .forEach((date) => {
                            if (date < startDateString || date > endDateString) {
                                return;
                            }
                            const uniformDate = gnoment(date)
                                .utc()
                                .format(types.AVAILABILITY_KEY_MAP_FORMAT);

                            // Set available true/false based on the filter value and max/min group size
                            if (quotaType === 'QuotaUsageByMemberDaily') {
                                resultingAvailableDays[uniformDate] =
                                    resultingAvailableDays[uniformDate] &&
                                    availabilitiesForQuotaType[date].remaining >=
                                        filterValue &&
                                    maxGroupSize >= filterValue &&
                                    minGroupSize <= filterValue;
                            } else {
                                resultingAvailableDays[uniformDate] =
                                    resultingAvailableDays[uniformDate] &&
                                    availabilitiesForQuotaType[date].remaining >=
                                        filterValue;
                            }

                            // and on lottery logic
                            resultingAvailableDays[uniformDate] =
                                resultingAvailableDays[uniformDate] &&
                                !isDayBlocked(gnoment(date).utc(), {
                                    permit,
                                    dateMap: resultingAvailableDays,
                                    blockLotteryDates: true,
                                    currentDate: gnoment().utc(),
                                    aggSeasonRules,
                                });

                            // and which availability to display
                            if (quotaType === selectedQuotaToDisplay) {
                                displayMap[uniformDate] =
                                    availabilitiesForQuotaType[date].remaining;
                            }
                        });
                }
            );

            return {
                permit,
                division,
                dateMap: resultingAvailableDays,
                secretMap,
                displayMap,
                quotas,
                aggSeasonRules,
                maxGroupSize,
            };
        });

        const divisionsForCurrentSection = this.getDivisionsForSection(result);

        this.setState({
            dataSource: result,
            divisionsForCurrentSection,
            didGenerateDataSource: true,
        });
    };

    getStayLimit() {
        // default value of 1.5 days so there's not bad availability data from a null stay limit
        const seasonStayLimitValue =
            get(this.props, 'divisionStayLimitRule.value', 0) / 24 / 60 / 60;

        return seasonStayLimitValue === 0 ? this.state.stayLimit : seasonStayLimitValue;
    }

    updatePereMarquetteFilter(issuance, divide) {
        // overwrite equipments if we are Pere Marquette
        let { equipments } = this.state;
        equipments = equipments.map((e) => {
            if (divide) {
                e.weight /= 2;
            } else {
                e.weight *= 2;
            }
            return e;
        });
        const updatedIssuance = {
            ...issuance,
            ...addFilterValues(this.state.quotaFilterValues),
            ...(equipments && equipments.length && { equipments }),
        };
        return updatedIssuance;
    }

    updateQuotaFilterType(quotaType) {
        this.setState(
            {
                selectedQuotaToDisplay: quotaType,
            },
            () => {
                this.generateDataSource();
            }
        );
        window.sessionStorage.setItem(
            'selectedQuotaToDisplay',
            JSON.stringify(quotaType)
        );
    }

    updateQuotaFilterValue(quotaType, newValues) {
        const newState = {};
        const newValue = newValues.value;
        const quotaFilterValues = { ...this.state.quotaFilterValues };

        // handle updated value
        if (newValue || newValue === '' || newValue === 0) {
            quotaFilterValues[quotaType] = newValue;
            newState.quotaFilterValues = quotaFilterValues;

            // reset date selection if increasing value
            if (this.state.existingReservationId) {
                const oldValue = this.state.initialQuotaFilterValues[quotaType];
                if (newValue > oldValue) {
                    this.resetSelection();
                }
            }
        }

        // handle equipments for pere marquette
        if (newValues.equipments) {
            newState.equipments = newValues.equipments;
        }

        this.setState(newState, () => {
            this.generateDataSource();

            this.setState({
                isProcessing: false,
            });
        });

        window.sessionStorage.setItem(
            'quotaFilterValues',
            JSON.stringify(newState.quotaFilterValues)
        );
        window.sessionStorage.setItem(
            'equipments',
            JSON.stringify(newState.equipments || this.state.equipments)
        );
    }

    getInformationRequirements = () => {
        const { selectedDistricts, quotaFilterValues, selectedSection } = this.state;
        const { permit } = this.props;
        const { permitId } = this.props.router.params;

        // Permit needs a "Group size" picker
        const hasSizeRequirement =
            !isNil(quotaFilterValues.QuotaUsageByMemberDaily) ||
            !isNil(quotaFilterValues.QuotaUsageByMember);
        const isSizeEntered =
            hasSizeRequirement &&
            (quotaFilterValues.QuotaUsageByMemberDaily > 0 ||
                quotaFilterValues.QuotaUsageByMember > 0);

        // Permit has sections and needs a "Permit type" picker
        const hasSectionRequirement = get(
            permit,
            'components.permit_section_filter',
            false
        );
        const isSectionEntered = hasSectionRequirement && !!selectedSection;

        // Permit needs an area/starting area picker
        const hasAreaRequirement = PERMITS_WITH_AREA_REQUIREMENT.includes(permitId);
        const isAreaEntered = hasAreaRequirement && selectedDistricts.length > 0;
        const divisionType = get(Object.values(permit.divisions), '[0].type', 'Division');

        // Determine if grid should be shown based on user selections
        this.shouldShowGrid = true;

        if (hasSizeRequirement && !isSizeEntered) {
            this.shouldShowGrid = false;
        }

        if (hasSectionRequirement && !isSectionEntered) {
            this.shouldShowGrid = false;
        }

        if (hasAreaRequirement && !isAreaEntered) {
            this.shouldShowGrid = false;
        }

        return {
            isRequired: !this.shouldShowGrid,
            divisionType,
            hasTypeRequirement: hasSectionRequirement && !selectedSection,
            hasSizeRequirement: hasSizeRequirement && !isSizeEntered,
            hasStartingAreaRequirement: hasAreaRequirement && !selectedDistricts.length,
        };
    };

    handleNotificationWarningClose = () =>
        this.setState({ displayWarning: false, warning: '' });

    renderFilterHeader() {
        const {
            guidesCountTowardsGroupSize,
            selectedDistricts,
            isGroup,
            contactFacility,
        } = this.state;
        let activeDivisions = [];
        const permitSections = get(this.props, 'permit.permit_sections');
        const hasNoSectionFilterComponent = !get(
            this.props,
            'permit.components.permit_section_filter'
        );
        const { permitId } = this.props.router.params;
        const requiresDistrictPicker = PERMITS_WITH_DISTRICT_PICKER.includes(permitId);
        const infoRequirements = this.getInformationRequirements();

        if (permitSections) {
            const section = permitSections.find(
                (sec) => sec.name === this.state.selectedSection
            );
            activeDivisions = get(section, 'division_ids', []);
        }

        return (
            <DetailedAvailabilityFilter>
                <LayoutControlContext.Consumer>
                    {(contextValue) => {
                        const { isMobileApp } = contextValue;
                        const AvailabilityFilterContainer = isMobileApp ? Stack : Inline;
                        const hasTypeQuestion = get(
                            this.props,
                            'permit.components.reservation_type_question'
                        );
                        const showFilters =
                            (!isGroup && contactFacility && isMobileApp) ||
                            !hasTypeQuestion ||
                            !isMobileApp;
                        return (
                            <AvailabilityFilterContainer>
                                {hasTypeQuestion && isMobileApp && (
                                    <OrganizedGroupQuestion
                                        contactFacility={this.state.contactFacility}
                                        handleAnswerChange={this.handleQuestionResponse}
                                    />
                                )}
                                {!hasNoSectionFilterComponent && showFilters && (
                                    <DetailedAvailabilityFilterItem mobileLabel="Select Permit type">
                                        <Select
                                            id="division-selection"
                                            label="Permit type"
                                            isLabelVisible={false}
                                            placeholder="Select Permit Type..."
                                            value={this.state.selectedSection || ''}
                                            onChange={this.handleSectionSelection}
                                            options={this.getSectionOptions()}
                                        />
                                    </DetailedAvailabilityFilterItem>
                                )}

                                {showFilters && (
                                    <DetailedAvailabilityFilterItem mobileLabel="Select Date">
                                        <DatePicker
                                            id="jump-date"
                                            isLabelVisible={false}
                                            label={'Select Date'}
                                            numVisibleMonths={1}
                                            defaultValue={gnomentToCalendarDate(
                                                this.state.detailedAvailabilityStartDate
                                            )}
                                            onChange={(day) =>
                                                this.handleStartDateChange(
                                                    calendarDateToGnoment(day)
                                                )
                                            }
                                            minValue={localToday}
                                        />
                                    </DetailedAvailabilityFilterItem>
                                )}

                                {showFilters && (
                                    <AvailabilityFilters
                                        permitID={this.props.permit.id}
                                        equipments={this.state.equipments}
                                        quotaFilters={readQuotaFilters(
                                            this.props.availabilities,
                                            this.props.permit,
                                            activeDivisions,
                                            SecurityHelper.isCommercialAccount()
                                        )}
                                        quotaFilterValues={this.state.quotaFilterValues}
                                        guidesCountTowardsGroupSize={
                                            guidesCountTowardsGroupSize &&
                                            SecurityHelper.isCommercialAccount()
                                        }
                                        selectedQuotaToDisplay={
                                            this.state.selectedQuotaToDisplay
                                        }
                                        detailedAvailabilityStartDate={
                                            this.state.detailedAvailabilityStartDate
                                        }
                                        jumpDateFocused={this.state.jumpDateFocused}
                                        updateQuotaFilterType={this.updateQuotaFilterType}
                                        updateQuotaFilterValue={
                                            this.updateQuotaFilterValue
                                        }
                                        handleJumpDateFocusChange={
                                            this.handleJumpDateFocusChange
                                        }
                                        setIsProcessing={this.setIsProcessing}
                                        isGuestCounterDisabled={
                                            infoRequirements.hasTypeRequirement &&
                                            !this.state.selectedSection
                                        }
                                    />
                                )}

                                {isMobileApp && requiresDistrictPicker && showFilters && (
                                    <OlympicDistrictPicker
                                        permitId={permitId}
                                        selectedDistricts={selectedDistricts}
                                        handleDistrictFilterClick={
                                            this.handleDistrictFilterClick
                                        }
                                    />
                                )}
                                {isMobileApp &&
                                    infoRequirements.isRequired &&
                                    showFilters && (
                                        <DetailedAvailabilityInformationRequired
                                            {...infoRequirements}
                                        />
                                    )}
                            </AvailabilityFilterContainer>
                        );
                    }}
                </LayoutControlContext.Consumer>
            </DetailedAvailabilityFilter>
        );
    }

    getErrorNotificationProps = () => {
        const errorsList = errorsToStringList([
            this.props.error,
            this.props.issuanceError,
            this.state.lotteryOnlyError,
        ]);
        if (errorsList[0]) {
            return {
                body: errorsList[0],
                isShow: true,
                dismiss: undefined,
            };
        }
        return {
            body: undefined,
            isShow: false,
            dismiss: undefined,
        };
    };

    renderMain() {
        const { name, id: permitId } = this.props.permit;
        const { selectedDistricts } = this.state;
        const path = [
            { title: 'Home', url: '/' },
            { title: name, url: `/permits/${permitId}` },
            { title: 'Detailed Availability' },
        ];

        // Permit requires district picker
        const requiresDistrictPicker = PERMITS_WITH_DISTRICT_PICKER.includes(permitId);
        const infoRequirements = this.getInformationRequirements();
        const { body, dismiss, isShow } = this.getErrorNotificationProps();
        const hasTypeQuestion = get(
            this.props,
            'permit.components.reservation_type_question'
        );
        const showCalendar =
            (!this.state.isGroup && !!this.state.contactFacility) || !hasTypeQuestion;

        return (
            <DetailedAvailabilityPageMain
                breadcrumbPath={path}
                warningBody={this.state.warning}
                warningIsShow={this.state.displayWarning}
                warningDismiss={this.handleNotificationWarningClose}
                errorBody={body}
                errorIsShow={isShow}
                errorDismiss={dismiss}
            >
                {this.renderFilterHeader()}

                <LayoutControlContext.Consumer isAllowOnDetailedAvailability>
                    {({ isMobileApp }) =>
                        !isMobileApp && (
                            <>
                                {hasTypeQuestion && (
                                    <OrganizedGroupQuestion
                                        isAllowOnDetailedAvailability
                                        contactFacility={this.state.contactFacility}
                                        handleAnswerChange={this.handleQuestionResponse}
                                    />
                                )}

                                {requiresDistrictPicker && (
                                    <OlympicDistrictPicker
                                        permitId={permitId}
                                        selectedDistricts={selectedDistricts}
                                        handleDistrictFilterClick={
                                            this.handleDistrictFilterClick
                                        }
                                    />
                                )}
                            </>
                        )
                    }
                </LayoutControlContext.Consumer>

                {infoRequirements.isRequired && showCalendar && (
                    <DetailedAvailabilityInformationRequired {...infoRequirements} />
                )}
                {showCalendar &&
                    !this.state.lotteryOnlyError &&
                    this.renderCalendarSection()}
            </DetailedAvailabilityPageMain>
        );
    }

    skip(duration, temporalUnit) {
        const startDate = this.state.detailedAvailabilityStartDate;
        const newStartDate = startDate.clone().add(duration, temporalUnit);
        this.handleStartDateChange(newStartDate);
    }

    renderAvailabilityNavigator = (daysToSkip) => {
        const {
            detailedAvailabilityStartDate: startDate,
            detailedAvailabilityEndDate: endDate,
            selectedItinerary,
        } = this.state;
        return (
            <DetailedAvailabilityNavigator
                onPrev={() => this.skip(-daysToSkip, 'days')}
                onNext={() => this.skip(daysToSkip, 'days')}
                onNextAvailable={this.handleNextAvailableClick}
                prevText={`Prev ${daysToSkip} Days`}
                nextText={`Next ${daysToSkip} Days`}
                onClearSelection={
                    selectedItinerary.length ? this.handleClearSelection : undefined
                }
                startDate={startDate}
                endDate={endDate}
                isPrevDisabled={isDaySameAsTodayOnUTC(startDate)}
            />
        );
    };

    renderCalendarSection() {
        const {
            isProcessing,
            dataSource,
            sort,
            detailedAvailabilityStartDate: startDate,
            detailedAvailabilityEndDate: endDate,
            selectedItinerary,
            divisionsForCurrentSection,
            existingReservationId,
        } = this.state;
        const { permit, availabilities } = this.props;
        const { permitId } = this.props.router.params;
        const hasExistingReservation =
            typeof existingReservationId === 'string' &&
            existingReservationId.length > 0 &&
            !this.props.fetchIssuanceError;
        const isBookNowDisabled =
            !selectedItinerary?.length ||
            getFilterErrors(this.state.quotaFilterValues).length > 0;

        // Bad backend data can cause stayLimitMetadata to be an empty string instead of an object
        let stayLimitMetadata = get(this.props, 'divisionStayLimitRule.metadata', {});
        if (stayLimitMetadata === '') {
            stayLimitMetadata = {};
        }

        const { divisionType } = this.getInformationRequirements();

        const issuanceStatus = get(this.props, 'issuance.status');

        return (
            <DetailedAvailabilityGridAndActions>
                <LayoutControlContext.Consumer>
                    {(contextValue) => {
                        const { isMobileApp, isFullView } = contextValue;
                        const isSmallerGrid = shouldUseSmallerGrid(contextValue);
                        const daysToSkip = isSmallerGrid
                            ? types.AVAILABILITY_DAYS_TO_SKIP_MOBILE
                            : types.AVAILABILITY_DAYS_TO_SKIP;
                        const numOfCols = isSmallerGrid
                            ? types.AVAILABILITY_DAYS_TO_DISPLAY_MOBILE
                            : types.AVAILABILITY_DAYS_TO_DISPLAY;

                        return (
                            <>
                                {isMobileApp && (
                                    <Box
                                        className="mb-2 pt-1"
                                        background="extra-light-blue"
                                    >
                                        <AvailabilityTypes
                                            legendItems={{
                                                available: false,
                                                availableNumber: true,
                                                walkup: true,
                                                lottery: true,
                                                unavailable: true,
                                            }}
                                        />
                                    </Box>
                                )}

                                {this.renderAvailabilityNavigator(daysToSkip)}
                                <AvailabilityMonths
                                    numOfCols={numOfCols}
                                    startDate={startDate}
                                />
                                <AvailabilityGrid
                                    divisionType={divisionType}
                                    allowFlexibleItinerary={readRuleAsBoolean(
                                        types.FLEXIBLE_ITINERARY_ALLOWED,
                                        permit.rules,
                                        RULE_NOT_SPECIFIC_TO_DIVISION
                                    )}
                                    allowSameDayReservation={readRuleAsBoolean(
                                        types.IS_SAME_DAY_TRIP_ITINERARY_ALLOWED_RULE_NAME,
                                        permit.rules,
                                        RULE_NOT_SPECIFIC_TO_DIVISION
                                    )}
                                    checkoutNextDay={permit.components.checkout_next_day}
                                    currentDate={getCurrentTime(this.props)}
                                    dataSource={dataSource}
                                    endDate={endDate}
                                    onDivisionDetailsClick={
                                        this.handleDivisionDetailsClick
                                    }
                                    handleDisplayWarning={this.handleDisplayWarning}
                                    hasStayLimitRule={
                                        !!get(
                                            this.props,
                                            'divisionStayLimitRule.value'
                                        ) || !!this.state.itineraryStayLimitPerDivision
                                    }
                                    ignoreQuotaAfterFirstDay={shouldIgnoreQuotaAfterFirstDay(
                                        availabilities
                                    )}
                                    isFullView={isFullView}
                                    isLoading={
                                        isProcessing || !this.state.didGenerateDataSource
                                    }
                                    itineraryStartNextDay={shouldIncrementCheckoutDate(
                                        permit.rules,
                                        divisionsForCurrentSection
                                    )}
                                    numDistricts={
                                        Object.keys(get(this.props, 'availabilities', {}))
                                            .length
                                    }
                                    onSelectionChange={this.handleSelection}
                                    overlay={this.getOverlay()}
                                    permitId={permitId}
                                    rules={permit.rules}
                                    selectedItinerary={selectedItinerary}
                                    sort={sort}
                                    startDate={startDate}
                                    stayLimit={this.getStayLimit()}
                                    stayLimitMetadata={stayLimitMetadata}
                                    stayLimitRule={readRule(
                                        types.STAY_LIMIT_RULE_NAME,
                                        permit.rules,
                                        RULE_NOT_SPECIFIC_TO_DIVISION
                                    )}
                                    itineraryStayLimitPerDivision={
                                        this.state.itineraryStayLimitPerDivision
                                    }
                                    timeZone={permit.time_zone}
                                    shouldHideUnavailableSites={false}
                                    updateSort={this.handleUpdateSort}
                                    numOfCols={numOfCols}
                                    isSmallerGrid={isSmallerGrid}
                                >
                                    <Box className="pt-1" background="white">
                                        {this.renderAvailabilityNavigator(daysToSkip)}
                                        <AvailabilityMonths
                                            numOfCols={numOfCols}
                                            startDate={startDate}
                                        />
                                    </Box>
                                </AvailabilityGrid>

                                <BookNow
                                    hasExistingReservation={hasExistingReservation}
                                    isDisabled={isBookNowDisabled}
                                    isLoading={
                                        isProcessing || !this.state.didGenerateDataSource
                                    }
                                    numItineraryItems={
                                        this.state.selectedItinerary.length
                                    }
                                    onCancelClick={() => {
                                        this.props.router.navigate(-1);
                                    }}
                                    onSaveClick={this.handleRegistration}
                                    issuanceStatus={issuanceStatus}
                                />
                            </>
                        );
                    }}
                </LayoutControlContext.Consumer>
            </DetailedAvailabilityGridAndActions>
        );
    }

    renderSide() {
        const divisions = Object.keys(this.props.permit.divisions);
        const entryDateOnly = isEntryDateOnly(this.props.permit.rules, divisions);
        return (
            <DetailedAvailabilityPageSide>
                <TripItinerary
                    renderFullItinerary={!entryDateOnly}
                    equipments={this.state.equipments}
                    itinerary={this.state.selectedItinerary}
                    existingItinerary={this.state.existingItinerary}
                    renderExistingItinerary={
                        this.state.existingReservationId !== '' &&
                        this.state.existingItinerary.length > 0
                    }
                    incrementCheckoutDate={shouldIncrementCheckoutDate(
                        this.props.permit.rules,
                        this.state.divisionsForCurrentSection
                    )}
                    name={this.props.permit.name}
                    errors={getFilterErrors(this.state.quotaFilterValues)}
                />
            </DetailedAvailabilityPageSide>
        );
    }

    handleCloseUnauthorizedErrorModal = () => {
        this.setState({ showCommercialAuthModal: false });
        this.props.router.navigate(`/permits/${this.props.permit.id}`);
    };

    render() {
        if (!this.state.lotteryOnlyError) {
            // if there's this error, let the users see the page w/ the error msg
            if (
                !this.state.hasSeededQuotaValues ||
                !this.props.permit.id ||
                this.props.router.params.permitId !== this.props.permit.id
            ) {
                return (
                    <div className="permit-loading-wrapper">
                        <Spinner />
                    </div>
                );
            }
        }
        const { isRequired } = this.getInformationRequirements();
        return (
            <DetailedAvailabilityPageLayout isInformationRequired={isRequired}>
                <DivisionDetailsModal
                    permit={this.props.permit}
                    divisionId={this.state.selectedDivisionId}
                    isOpen={this.state.isDivisionDetailsModalOpen}
                    onRequestClose={this.closeDivisionDetailsModal}
                    permitMapping={this.props.permitMapping}
                />
                {this.renderMain()}
                {this.renderSide()}
                <UnauthorizedErrorModal
                    isOpen={this.state.showCommercialAuthModal}
                    closeModal={this.handleCloseUnauthorizedErrorModal}
                />
                <RecaptchaChallengeModal
                    isOpen={this.state.isChallengeModalOpen}
                    onClose={() => this.setState({ isChallengeModalOpen: false })}
                    onContinue={() => this.navigateToRegistration()}
                    recaptcha={this.props.recaptcha}
                    isSubmitting={this.state.isLoading}
                    action={recaptchaConstants.CH_AVAILABILITY_BOOKING}
                />
            </DetailedAvailabilityPageLayout>
        );
    }
}

function mapStateToProps(state) {
    return {
        permit: state.permit.result,
        account: state.auth.account,
        issuance: state.permit.issuance,
        availabilities: state.permit.availabilities,
        divisionStayLimitRule: state.permit.rule_staylimit,
        error: state.permit.error,
        issuanceError: state.permit.issuanceError,
        fetchIssuanceError: state.permit.fetch_issuance_error,
        updateIssuanceError: state.permit.update_issuance_error,
        updateIssuanceResult: get(state, 'permit.update_issuance_result'),
        aggSeasonRules: state.permit.agg_season_rules,
        permitMapping: permitContentSelector.permitMapping(state),
    };
}

export default withCompatRouter(
    connect(mapStateToProps)(withRecaptchaFallback(DetailedAvailabilityPage))
);
